// src/pages/finance/Accounting.js
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  FormControl,
  Select,
  MenuItem,
  Stack,
  IconButton,
  Tooltip,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Zoom,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  Alert,
  Snackbar,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Tab,
  Tabs,
  Chip
} from '@mui/material';
import {
  AccountBalance as BankIcon,
  AccountBalanceWallet as CashIcon,
  SwapHoriz as TransferIcon,
  AddCircle as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Download as ExportIcon,
  CheckCircle as CheckedIcon,
  TrendingUp as ProfitIcon,
  TrendingDown as LossIcon,
  PieChart as PieIcon,
  AttachMoney as MoneyIcon,
  Dns as DnsIcon,
  AdminPanelSettings as SettingsIcon,
  Category as CategoryIcon
} from '@mui/icons-material';

const colors = {
  grey: '#64748b',
};

const StatCard = ({ title, value, icon: Icon, color, subtitle, delay = 0 }) => (
  <Zoom in={true} style={{ transitionDelay: `${delay * 100}ms` }}>
    <Card
      sx={{
        position: 'relative',
        overflow: 'hidden',
        borderRadius: '12px',
        background: 'rgba(255, 255, 255, 0.8)',
        backdropFilter: 'blur(12px)',
        border: '1px solid rgba(255, 255, 255, 0.4)',
        boxShadow: '0 4px 16px 0 rgba(31, 38, 135, 0.03)',
        borderLeft: `4px solid ${color}`,
        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 8px 24px 0 rgba(31, 38, 135, 0.08)',
        }
      }}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="caption" sx={{ color: colors.grey, fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', fontSize: '0.68rem' }}>
              {title}
            </Typography>
            <Typography variant="h5" sx={{ mt: 0.5, fontWeight: 800, color: '#1e293b', fontSize: '1.25rem' }}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="caption" sx={{ color: colors.grey, display: 'block', marginTop: '2px', fontWeight: 500, fontSize: '0.7rem' }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              width: '38px',
              height: '38px',
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: `${color}15`,
              color: color,
              '& svg': { fontSize: 20 },
              flexShrink: 0
            }}
          >
            {Icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  </Zoom>
);

const Accounting = () => {
  const [activeSubTab, setActiveSubTab] = useState(0);
  const [accounts, setAccounts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionInProgress, setActionInProgress] = useState(false);
  const [saving, setSaving] = useState(false);

  // Estados de Filtro de Fechas Contables
  const [startDate, setStartDate] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().substring(0, 10));
  const [endDate, setEndDate] = useState(new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toISOString().substring(0, 10));

  // Estados del Reporte P&L
  const [pnlReport, setPnlReport] = useState({
    incomes: [],
    expenses: [],
    totalIncome: 0,
    totalExpense: 0,
    netProfit: 0
  });

  // Modales
  const [openAccountModal, setOpenAccountModal] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [openTransferModal, setOpenTransferModal] = useState(false);
  const [openCategoryModal, setOpenCategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);

  // Formularios
  const [accountForm, setAccountForm] = useState({
    name: '',
    type: 'cash',
    account_number: '',
    initial_balance: '0'
  });

  const [transferForm, setTransferForm] = useState({
    source_account_id: '',
    target_account_id: '',
    amount: '',
    notes: ''
  });

  const [categoryForm, setCategoryForm] = useState({
    name: '',
    type: 'expense',
    description: ''
  });

  // Notificaciones Toast
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

  const showToast = (message, severity = 'success') => {
    setToast({ open: true, message, severity });
  };

  const fetchPnLReport = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/expenses/reports/pnl?startDate=${startDate}&endDate=${endDate}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPnlReport(data);
      }
    } catch (err) {
      console.error('Error fetching P&L report:', err);
    }
  }, [startDate, endDate]);

  const fetchInitialData = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const [accountsRes, catsRes] = await Promise.all([
        fetch('/api/accounting/accounts', { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch('/api/accounting/categories', { headers: { 'Authorization': `Bearer ${token}` } })
      ]);

      if (accountsRes.ok && catsRes.ok) {
        const accountsData = await accountsRes.json();
        const catsData = await catsRes.json();
        setAccounts(accountsData);
        setCategories(catsData);
      } else {
        showToast('Error al cargar datos del servidor', 'error');
      }
    } catch (err) {
      console.error(err);
      showToast('Error de conexión', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  useEffect(() => {
    fetchPnLReport();
  }, [fetchPnLReport]);

  const handleOpenAddAccount = () => {
    setEditingAccount(null);
    setAccountForm({
      name: '',
      type: 'cash',
      account_number: '',
      initial_balance: '0'
    });
    setOpenAccountModal(true);
  };

  const handleOpenEditAccount = (acc) => {
    setEditingAccount(acc);
    setAccountForm({
      name: acc.name,
      type: acc.type,
      account_number: acc.account_number || '',
      initial_balance: acc.initial_balance.toString()
    });
    setOpenAccountModal(true);
  };

  const handleAccountSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const url = editingAccount ? `/api/accounting/accounts/${editingAccount.id}` : '/api/accounting/accounts';
      const method = editingAccount ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(accountForm)
      });

      if (response.ok) {
        showToast(editingAccount ? 'Cuenta modificada con éxito' : 'Cuenta creada con éxito', 'success');
        setOpenAccountModal(false);
        fetchInitialData();
        fetchPnLReport();
      } else {
        const data = await response.json();
        showToast(data.message || 'Error al guardar cuenta', 'error');
      }
    } catch (err) {
      console.error(err);
      showToast('Error al conectar con el servidor', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAccount = async (acc) => {
    if (!window.confirm(`¿Estás seguro de que deseas eliminar la cuenta "${acc.name}"? Esta acción validará que no posea gastos registrados.`)) {
      return;
    }
    setActionInProgress(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/accounting/accounts/${acc.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        showToast('Cuenta eliminada con éxito', 'success');
        fetchInitialData();
      } else {
        const data = await response.json();
        showToast(data.message || 'Error al eliminar cuenta', 'error');
      }
    } catch (err) {
      console.error(err);
      showToast('Error de conexión', 'error');
    } finally {
      setActionInProgress(false);
    }
  };

  const handleOpenTransfer = () => {
    const activeAccounts = accounts.filter(a => a.status === 'active');
    setTransferForm({
      source_account_id: activeAccounts.length > 0 ? activeAccounts[0].id : '',
      target_account_id: activeAccounts.length > 1 ? activeAccounts[1].id : '',
      amount: '',
      notes: ''
    });
    setOpenTransferModal(true);
  };

  const handleTransferSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/accounting/accounts/transfer', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(transferForm)
      });
      if (response.ok) {
        showToast('¡Fondos transferidos y conciliados exitosamente!', 'success');
        setOpenTransferModal(false);
        fetchInitialData();
        fetchPnLReport();
      } else {
        const data = await response.json();
        showToast(data.message || 'Error al transferir fondos', 'error');
      }
    } catch (err) {
      console.error(err);
      showToast('Error de conexión', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleOpenAddCategory = () => {
    setEditingCategory(null);
    setCategoryForm({
      name: '',
      type: activeSubTab === 0 ? 'expense' : 'income',
      description: ''
    });
    setOpenCategoryModal(true);
  };

  const handleOpenEditCategory = (cat) => {
    setEditingCategory(cat);
    setCategoryForm({
      name: cat.name,
      type: cat.type,
      description: cat.description || ''
    });
    setOpenCategoryModal(true);
  };

  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const url = editingCategory ? `/api/accounting/categories/${editingCategory.id}` : '/api/accounting/categories';
      const method = editingCategory ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(categoryForm)
      });
      if (response.ok) {
        showToast(editingCategory ? 'Categoría contable actualizada con éxito' : 'Categoría contable registrada con éxito', 'success');
        setOpenCategoryModal(false);
        fetchInitialData();
      } else {
        const data = await response.json();
        showToast(data.message || 'Error al guardar categoría', 'error');
      }
    } catch (err) {
      console.error(err);
      showToast('Error de conexión', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCategory = async (cat) => {
    if (!window.confirm(`¿Estás seguro de que deseas eliminar la categoría "${cat.name}"?`)) {
      return;
    }
    setActionInProgress(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/accounting/categories/${cat.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        showToast('Categoría contable eliminada con éxito', 'success');
        fetchInitialData();
      } else {
        const data = await response.json();
        showToast(data.message || 'Error al eliminar categoría', 'error');
      }
    } catch (err) {
      console.error(err);
      showToast('Error de conexión', 'error');
    } finally {
      setActionInProgress(false);
    }
  };

  const exportLedgerToCSV = () => {
    try {
      showToast('Generando libro diario para exportación...', 'info');
      // Crear CSV
      let csvContent = 'data:text/csv;charset=utf-8,';
      csvContent += 'Fecha,Rubro/Concepto,Tipo,Detalle,Monto (S/)\n';

      // Añadir ingresos
      pnlReport.incomes.forEach(inc => {
        csvContent += `Periodo,${inc.category},Ingreso,Cobro de Clientes WISP,${inc.total}\n`;
      });

      // Añadir gastos
      pnlReport.expenses.forEach(exp => {
        csvContent += `Periodo,${exp.category},Egreso,Gasto de Operacion / Proveedores,${exp.total}\n`;
      });

      const encodedUri = encodeURI(csvContent);
      const link = document.createElement('a');
      link.setAttribute('href', encodedUri);
      link.setAttribute('download', `libro_diario_${startDate}_a_${endDate}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      showToast('¡Libro diario exportado con éxito!', 'success');
    } catch (err) {
      console.error(err);
      showToast('Error al exportar libro diario', 'error');
    }
  };

  // Calcular efectivo total en cajas
  const totalCashBalance = accounts
    .filter(a => a.status === 'active')
    .reduce((sum, a) => sum + (a.current_balance || 0), 0);

  return (
    <Box sx={{ backgroundColor: '#f8fafc', minHeight: '100vh', p: { xs: 1, sm: 1.5, md: 2 } }}>
      
      {/* Cabecera Premium de Contabilidad */}
      <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} justifyItems="center" justifyContent="space-between" sx={{ mb: 2 }} gap={1.5}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a', letterSpacing: '-0.5px', fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
            Contabilidad y Cuentas Financieras 📊💼
          </Typography>
          <Typography variant="caption" sx={{ color: '#64748b', mt: 0.2, fontWeight: 500, display: 'block' }}>
            Visualiza balances corrientes de bancos, realiza transferencias conciliadas y audita tu Estado de Pérdidas y Ganancias (P&L) en tiempo real.
          </Typography>
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ alignSelf: { xs: 'stretch', md: 'center' } }}>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<CategoryIcon sx={{ fontSize: '18px' }} />}
            onClick={handleOpenAddCategory}
            sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 'bold', height: '36px', fontSize: '0.8125rem' }}
          >
            Nueva Categoría
          </Button>
          <Button
            variant="outlined"
            startIcon={<TransferIcon sx={{ fontSize: '18px' }} />}
            onClick={handleOpenTransfer}
            disabled={accounts.filter(a => a.status === 'active').length < 2}
            sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 'bold', height: '36px', fontSize: '0.8125rem' }}
          >
            Transferir Fondos
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon sx={{ fontSize: '18px' }} />}
            onClick={handleOpenAddAccount}
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 'bold',
              height: '36px',
              fontSize: '0.8125rem',
              boxShadow: 'none',
              backgroundColor: '#6366f1',
              '&:hover': { backgroundColor: '#4f46e5' }
            }}
          >
            Nueva Cuenta
          </Button>
        </Stack>
      </Box>

      {/* KPI Cards del periodo contable */}
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Efectivo en Caja (Bancos / Cajas)"
            value={`S/ ${totalCashBalance.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
            icon={<CashIcon />}
            color="#6366f1"
            subtitle="Saldo total disponible"
            delay={0}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Ingresos del Periodo"
            value={`S/ ${pnlReport.totalIncome.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
            icon={<ProfitIcon />}
            color="#10b981"
            subtitle="Cobro neto de recibos"
            delay={1}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Gastos del Periodo"
            value={`S/ ${pnlReport.totalExpense.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
            icon={<LossIcon />}
            color="#ef4444"
            subtitle="Egresos y facturas pagadas"
            delay={2}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Utilidad Neta del Periodo"
            value={`S/ ${pnlReport.netProfit.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
            icon={pnlReport.netProfit >= 0 ? <ProfitIcon /> : <LossIcon />}
            color={pnlReport.netProfit >= 0 ? '#10b981' : '#ef4444'}
            subtitle="Utilidad final libre de gastos"
            delay={3}
          />
        </Grid>
      </Grid>

      {/* Selector de Fechas y Filtro Contable */}
      <Paper sx={{ p: 1.5, borderRadius: '12px', mb: 2, border: '1px solid rgba(0,0,0,0.04)', boxShadow: 'none', backgroundColor: 'white' }}>
        <Grid container spacing={2} alignItems="center" justifyContent="space-between">
          <Grid item xs={12} sm={8} md={6}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <TextField
                type="date"
                label="Inicio del Ejercicio"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
                fullWidth
                InputProps={{ sx: { height: '36px', borderRadius: '8px' } }}
              />
              <Typography variant="caption" color="textSecondary">al</Typography>
              <TextField
                type="date"
                label="Fin del Ejercicio"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
                fullWidth
                InputProps={{ sx: { height: '36px', borderRadius: '8px' } }}
              />
            </Stack>
          </Grid>
          <Grid item xs={12} sm={4} md={3} display="flex" justifyContent="flex-end">
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<ExportIcon sx={{ fontSize: '18px' }} />}
              onClick={exportLedgerToCSV}
              sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 'bold', height: '36px', fontSize: '0.8125rem', width: { xs: '100%', sm: 'auto' } }}
            >
              Exportar Libro Diario 📥
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Cuerpo Principal */}
      <Grid container spacing={4}>
        
        {/* Panel Izquierdo: Cuentas y Categorías */}
        <Grid item xs={12} md={6}>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, color: '#1e293b' }}>
            💼 Cuentas de Caja Chica y Bancos
          </Typography>
          
          <Grid container spacing={2} sx={{ mb: 4 }}>
            {accounts.map(acc => (
              <Grid item xs={12} sm={6} key={acc.id}>
                <Card sx={{ 
                  borderRadius: '16px', 
                  border: '1px solid #e2e8f0', 
                  boxShadow: 'none', 
                  opacity: acc.status === 'active' ? 1 : 0.6,
                  transition: 'all 0.3s ease',
                  '&:hover': { transform: 'scale(1.01)', boxShadow: '0 8px 24px rgba(0,0,0,0.03)' }
                }}>
                  <CardContent sx={{ p: 2.5 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
                      <Avatar sx={{ 
                        bgcolor: acc.type === 'bank' ? '#eff6ff' : (acc.type === 'wallet' ? '#f0fdf4' : '#fff7ed'),
                        color: acc.type === 'bank' ? '#2563eb' : (acc.type === 'wallet' ? '#16a34a' : '#ea580c'),
                        borderRadius: '10px'
                      }}>
                        {acc.type === 'bank' ? <BankIcon /> : <CashIcon />}
                      </Avatar>
                      <Stack direction="row" spacing={0.5}>
                        <IconButton size="small" onClick={() => handleOpenEditAccount(acc)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDeleteAccount(acc)} disabled={actionInProgress}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </Box>
                    
                    <Typography variant="subtitle1" fontWeight="bold" color="textPrimary">
                      {acc.name}
                    </Typography>
                    {acc.account_number && (
                      <Typography variant="caption" color="textSecondary" sx={{ fontFamily: 'monospace', display: 'block', mt: 0.5 }}>
                        N°: {acc.account_number}
                      </Typography>
                    )}
                    
                    <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
                      <Typography variant="body2" color="textSecondary" fontWeight="500">
                        Saldo Corriente:
                      </Typography>
                      <Typography variant="h6" fontWeight="bold" color={acc.current_balance >= 0 ? '#10b981' : '#ef4444'} sx={{ fontFamily: 'monospace' }}>
                        S/ {acc.current_balance.toFixed(2)}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Listado de Categorías Contables */}
          <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h6" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#1e293b' }}>
              🏷️ Categorías Contables y Rubros
            </Typography>
            <Button
              size="small"
              variant="outlined"
              color="secondary"
              startIcon={<AddIcon fontSize="small" />}
              onClick={handleOpenAddCategory}
              sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 'bold' }}
            >
              Nueva Categoría
            </Button>
          </Box>
          <Paper sx={{ p: 2.5, borderRadius: '20px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
            <Tabs 
              value={activeSubTab} 
              onChange={(e, val) => setActiveSubTab(val)} 
              sx={{ borderBottom: '1px solid #e2e8f0', mb: 2 }}
              textColor="primary"
              indicatorColor="primary"
            >
              <Tab label="Egresos (Gastos)" size="small" sx={{ textTransform: 'none', fontWeight: 'bold' }} />
              <Tab label="Ingresos" size="small" sx={{ textTransform: 'none', fontWeight: 'bold' }} />
            </Tabs>

            <List sx={{ maxHeight: 300, overflowY: 'auto' }}>
              {categories
                .filter(c => c.type === (activeSubTab === 0 ? 'expense' : 'income'))
                .map(cat => (
                  <ListItem key={cat.id} sx={{ px: 1, borderBottom: '1px solid #f1f5f9' }}>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: activeSubTab === 0 ? '#fef2f2' : '#f0fdf4', color: activeSubTab === 0 ? '#ef4444' : '#10b981' }}>
                        <CategoryIcon fontSize="small" />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText 
                      primary={cat.name} 
                      secondary={cat.description || 'Sin descripción'} 
                      primaryTypographyProps={{ fontWeight: 'bold', fontSize: '0.9rem' }}
                      secondaryTypographyProps={{ fontSize: '0.75rem' }}
                    />
                    {!cat.is_system && (
                      <Stack direction="row" spacing={0.5}>
                        <IconButton size="small" color="primary" onClick={() => handleOpenEditCategory(cat)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDeleteCategory(cat)} disabled={actionInProgress}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    )}
                  </ListItem>
                ))}
            </List>
          </Paper>
        </Grid>

        {/* Panel Derecho: Estado de Pérdidas y Ganancias (P&L) */}
        <Grid item xs={12} md={6}>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, color: '#1e293b' }}>
            📊 Estado de Pérdidas y Ganancias (P&L)
          </Typography>

          <Paper sx={{ p: 4, borderRadius: '24px', border: '1px solid #e2e8f0', boxShadow: 'none', backgroundColor: 'white' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <Typography variant="subtitle2" color="textSecondary" fontWeight="700" sx={{ textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Detalle de Ingresos y Gastos
              </Typography>
              <Chip label={`${startDate} al ${endDate}`} size="small" color="primary" variant="outlined" sx={{ fontWeight: 600 }} />
            </Box>

            {/* SECCIÓN DE INGRESOS */}
            <Typography variant="subtitle2" color="success.main" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              (+) INGRESOS OPERATIVOS
            </Typography>
            <List disablePadding sx={{ mb: 3 }}>
              {pnlReport.incomes.map(item => (
                <ListItem key={item.category} sx={{ py: 1, px: 0, justifyContent: 'space-between', borderBottom: '1px dashed #e2e8f0' }}>
                  <Typography variant="body2" sx={{ color: '#475569', fontWeight: 500 }}>{item.category}</Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 'bold', color: '#1e293b' }}>
                    S/ {item.total.toFixed(2)}
                  </Typography>
                </ListItem>
              ))}
              {pnlReport.incomes.length === 0 && (
                <Typography variant="caption" color="textSecondary" display="block" sx={{ py: 1 }}>No se registraron ingresos en este periodo.</Typography>
              )}
              <Box display="flex" justifyContent="space-between" mt={1.5} px={1} py={1} sx={{ bgcolor: '#f0fdf4', borderRadius: '8px' }}>
                <Typography variant="body2" color="success.dark" fontWeight="bold">Total Ingresos Operativos</Typography>
                <Typography variant="body2" color="success.dark" fontWeight="bold" sx={{ fontFamily: 'monospace' }}>
                  S/ {pnlReport.totalIncome.toFixed(2)}
                </Typography>
              </Box>
            </List>

            <Divider sx={{ my: 3 }} />

            {/* SECCIÓN DE EGRESOS */}
            <Typography variant="subtitle2" color="error.main" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              (-) EGRESOS OPERATIVOS / GASTOS
            </Typography>
            <List disablePadding sx={{ mb: 3 }}>
              {pnlReport.expenses.map(item => (
                <ListItem key={item.category} sx={{ py: 1, px: 0, justifyContent: 'space-between', borderBottom: '1px dashed #e2e8f0' }}>
                  <Typography variant="body2" sx={{ color: '#475569', fontWeight: 500 }}>{item.category}</Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 'bold', color: '#1e293b' }}>
                    S/ {item.total.toFixed(2)}
                  </Typography>
                </ListItem>
              ))}
              {pnlReport.expenses.length === 0 && (
                <Typography variant="caption" color="textSecondary" display="block" sx={{ py: 1 }}>No se registraron gastos en este periodo.</Typography>
              )}
              <Box display="flex" justifyContent="space-between" mt={1.5} px={1} py={1} sx={{ bgcolor: '#fef2f2', borderRadius: '8px' }}>
                <Typography variant="body2" color="error.dark" fontWeight="bold">Total Egresos Operativos</Typography>
                <Typography variant="body2" color="error.dark" fontWeight="bold" sx={{ fontFamily: 'monospace' }}>
                  S/ {pnlReport.totalExpense.toFixed(2)}
                </Typography>
              </Box>
            </List>

            {/* SECCIÓN DE RESULTADO FINAL */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mt={4} sx={{ p: 2.5, borderRadius: '16px', bgcolor: pnlReport.netProfit >= 0 ? '#d1fae5' : '#fee2e2', color: pnlReport.netProfit >= 0 ? '#065f46' : '#991b1b', border: `1px solid ${pnlReport.netProfit >= 0 ? '#a7f3d0' : '#fecaca'}` }}>
              <Box>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {pnlReport.netProfit >= 0 ? '🏆 UTILIDAD NETA DEL PERIODO' : '⚠️ DÉFICIT DEL PERIODO'}
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.8 }}>
                  {pnlReport.netProfit >= 0 ? '¡Excelente rendimiento financiero!' : 'Los egresos superaron a los ingresos en este rango.'}
                </Typography>
              </Box>
              <Typography variant="h5" fontWeight="bold" sx={{ fontFamily: 'monospace' }}>
                S/ {pnlReport.netProfit.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </Typography>
            </Box>

          </Paper>
        </Grid>

      </Grid>

      {/* DIÁLOGO MODAL: NUEVA / EDITAR CUENTA */}
      <Dialog open={openAccountModal} onClose={() => setOpenAccountModal(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 4 } }}>
        <DialogTitle sx={{ fontWeight: 'bold', borderBottom: '1px solid #e2e8f0' }}>
          {editingAccount ? '✏️ Editar Cuenta Financiera' : '💼 Crear Nueva Cuenta Financiera'}
        </DialogTitle>
        <form onSubmit={handleAccountSubmit}>
          <DialogContent sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <TextField
              fullWidth
              required
              label="Nombre de la Cuenta"
              name="name"
              value={accountForm.name}
              onChange={(e) => setAccountForm(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Ej: BCP Corriente, Caja Chica Oficina"
            />
            <TextField
              select
              fullWidth
              required
              label="Tipo de Cuenta"
              name="type"
              value={accountForm.type}
              onChange={(e) => setAccountForm(prev => ({ ...prev, type: e.target.value }))}
            >
              <MenuItem value="cash">💵 Caja Chica (Efectivo)</MenuItem>
              <MenuItem value="bank">🏛️ Banco (Cuenta de Ahorros / Corriente)</MenuItem>
              <MenuItem value="wallet">📱 Billetera Digital (Yape / Plin Corporativo)</MenuItem>
            </TextField>
            <TextField
              fullWidth
              label="Número de Cuenta / Celular"
              name="account_number"
              value={accountForm.account_number}
              onChange={(e) => setAccountForm(prev => ({ ...prev, account_number: e.target.value }))}
              placeholder="Ej: 191-12345678-0-12, 987654321"
            />
            <TextField
              fullWidth
              required
              type="number"
              label="Saldo Inicial"
              name="initial_balance"
              value={accountForm.initial_balance}
              onChange={(e) => setAccountForm(prev => ({ ...prev, initial_balance: e.target.value }))}
              disabled={!!editingAccount}
              InputProps={{
                startAdornment: <InputAdornment position="start">S/</InputAdornment>
              }}
            />
          </DialogContent>
          <DialogActions sx={{ p: 3, borderTop: '1px solid #e2e8f0', gap: 1.5 }}>
            <Button onClick={() => setOpenAccountModal(false)} variant="outlined" sx={{ borderRadius: 2, textTransform: 'none' }}>
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={saving}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                backgroundColor: '#6366f1',
                '&:hover': { backgroundColor: '#4f46e5' }
              }}
            >
              {saving ? 'Guardando...' : 'Guardar Cuenta'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* DIÁLOGO MODAL: TRANSFERIR FONDOS */}
      <Dialog open={openTransferModal} onClose={() => setOpenTransferModal(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 4 } }}>
        <DialogTitle sx={{ fontWeight: 'bold', borderBottom: '1px solid #e2e8f0' }}>
          <TransferIcon sx={{ mr: 1, verticalAlign: 'middle', color: '#10b981' }} />
          Transferir Fondos entre Cuentas
        </DialogTitle>
        <form onSubmit={handleTransferSubmit}>
          <DialogContent sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            
            <TextField
              select
              fullWidth
              required
              label="Cuenta de Origen (Sale dinero)"
              name="source_account_id"
              value={transferForm.source_account_id}
              onChange={(e) => setTransferForm(prev => ({ ...prev, source_account_id: e.target.value }))}
            >
              {accounts.filter(a => a.status === 'active').map(acc => (
                <MenuItem key={acc.id} value={acc.id}>
                  {acc.name} (Saldo: S/ {acc.current_balance.toFixed(2)})
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              fullWidth
              required
              label="Cuenta de Destino (Ingresa dinero)"
              name="target_account_id"
              value={transferForm.target_account_id}
              onChange={(e) => setTransferForm(prev => ({ ...prev, target_account_id: e.target.value }))}
            >
              {accounts.filter(a => a.status === 'active').map(acc => (
                <MenuItem key={acc.id} value={acc.id}>
                  {acc.name} (Saldo: S/ {acc.current_balance.toFixed(2)})
                </MenuItem>
              ))}
            </TextField>

            <TextField
              fullWidth
              required
              type="number"
              label="Monto a Transferir"
              name="amount"
              value={transferForm.amount}
              onChange={(e) => setTransferForm(prev => ({ ...prev, amount: e.target.value }))}
              inputProps={{ min: '0.01', step: '0.01' }}
              InputProps={{
                startAdornment: <InputAdornment position="start">S/</InputAdornment>
              }}
            />

            <TextField
              fullWidth
              label="Notas / Descripción"
              name="notes"
              value={transferForm.notes}
              onChange={(e) => setTransferForm(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Ej: Traspaso a caja chica semanal"
            />

          </DialogContent>
          <DialogActions sx={{ p: 3, borderTop: '1px solid #e2e8f0', gap: 1.5 }}>
            <Button onClick={() => setOpenTransferModal(false)} variant="outlined" sx={{ borderRadius: 2, textTransform: 'none' }}>
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={saving}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                backgroundColor: '#10b981',
                '&:hover': { backgroundColor: '#059669' }
              }}
            >
              {saving ? 'Transfiriendo...' : 'Ejecutar Transferencia'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* DIÁLOGO MODAL: NUEVA / EDITAR CATEGORÍA CONTABLE */}
      <Dialog open={openCategoryModal} onClose={() => setOpenCategoryModal(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 4 } }}>
        <DialogTitle sx={{ fontWeight: 'bold', borderBottom: '1px solid #e2e8f0' }}>
          <CategoryIcon sx={{ mr: 1, verticalAlign: 'middle', color: '#8b5cf6' }} />
          {editingCategory ? 'Editar Categoría Contable' : 'Nueva Categoría / Rubro Contable'}
        </DialogTitle>
        <form onSubmit={handleCategorySubmit}>
          <DialogContent sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <TextField
              fullWidth
              required
              label="Nombre de la Categoría"
              name="name"
              value={categoryForm.name}
              onChange={(e) => setCategoryForm(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Ej: Torres de Comunicaciones, Comisión Ventas"
            />
            <TextField
              select
              fullWidth
              required
              disabled={!!editingCategory}
              label="Tipo de Categoría"
              name="type"
              value={categoryForm.type}
              onChange={(e) => setCategoryForm(prev => ({ ...prev, type: e.target.value }))}
            >
              <MenuItem value="expense">📉 Egreso (Gasto de la Empresa)</MenuItem>
              <MenuItem value="income">📈 Ingreso (Entrada de Dinero)</MenuItem>
            </TextField>
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Descripción"
              name="description"
              value={categoryForm.description}
              onChange={(e) => setCategoryForm(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Escribe detalles breves de qué agrupa esta categoría..."
            />
          </DialogContent>
          <DialogActions sx={{ p: 3, borderTop: '1px solid #e2e8f0', gap: 1.5 }}>
            <Button onClick={() => setOpenCategoryModal(false)} variant="outlined" sx={{ borderRadius: 2, textTransform: 'none' }}>
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={saving}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                backgroundColor: '#8b5cf6',
                '&:hover': { backgroundColor: '#7c3aed' }
              }}
            >
              {saving ? 'Guardando...' : (editingCategory ? 'Actualizar Categoría' : 'Crear Categoría')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Toast Notification */}
      <Snackbar
        open={toast.open}
        autoHideDuration={6000}
        onClose={() => setToast(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setToast(prev => ({ ...prev, open: false }))}
          severity={toast.severity}
          variant="filled"
          sx={{ width: '100%', borderRadius: 2 }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Accounting;
